"use client";

import { cn } from "../../lib/utils";
import { BottomSheet } from "./bottom-sheet";
import { Button } from "./button";
import { CopyIcon, RoundCheckIcon } from "../icons";
import { RotateCw } from "lucide-react";
import * as React from "react";

import { stripCopyMarkdown } from "@artha/utils";
import {
  POST_STUDIO_GENERATION_STAGE,
  GENERATION_STAGE_ORDER,
  POST_STUDIO_COPIED_DURATION_MS,
} from "../post-studio/post-studio-generation-sheet.constants";
import type { PostStudioGenerationSheetProps } from "../post-studio/post-studio-generation-sheet.types";
export { POST_STUDIO_GENERATION_STAGE } from "../post-studio/post-studio-generation-sheet.constants";
export type {
  PostStudioGenerationStage,
  PostStudioGenerationStageMap,
  PostStudioGenerationSheetProps,
} from "../post-studio/post-studio-generation-sheet.types";

export function PostStudioGenerationSheet({
  isOpen,
  selectedPlatformLabel,
  generationStage,
  generatedContent,
  title,
  subtitle,
  draftReadyFallback,
  regenerateLabel,
  stageLabels,
  stageTitles,
  stageDescriptions,
  copyActionLabel,
  readyHeading,
  stayOnPageLabel,
  backLabel = "Back",
  progress = 0,
  onBack,
  onClose,
  onRegenerate,
  onCopyAndGo,
  isRegenerateDisabled,
  copyDisabled,
  copyControls,
  copyErrorMessage,
}: PostStudioGenerationSheetProps) {
  const [isCopied, setIsCopied] = React.useState(false);

  const [copyFailed, setCopyFailed] = React.useState(false);

  const handleCopyContent = async () => {
    if (!generatedContent?.body || copyDisabled) return;
    setCopyFailed(false);
    try {
      const cleanContent = stripCopyMarkdown(generatedContent.body);
      await navigator.clipboard.writeText(cleanContent);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), POST_STUDIO_COPIED_DURATION_MS);
    } catch {
      setCopyFailed(true);
    }
  };
  const renderContent = (text: string) => {
    const cleanText = stripCopyMarkdown(text);
    return cleanText.split("\n").map((line, i) => (
      <p key={i} className="mb-3 last:mb-0 leading-7 text-[#475467]">
        {line}
      </p>
    ));
  };
  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      showDragHandle
      className="bg-[#FFF9EF] px-4 pb-6 pt-4 max-h-[80vh] shadow-[0_-12px_76px_rgba(0,0,0,0.16)] "
      contentClassName="fixed! bottom-0! max-w-none"
      title={
        <div className="space-y-2">
          <button
            type="button"
            onClick={onBack ?? onClose}
            aria-label={backLabel}
            className="inline-flex items-center gap-2 text-xl font-semibold text-[#344054]"
          >
            <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M9.5 4.5L6.5 8L9.5 11.5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            <span>{selectedPlatformLabel ? title : draftReadyFallback}</span>
          </button>
          <p className="text-sm font-normal leading-6 text-[#667085]">{subtitle}</p>
        </div>
      }
    >
      {generationStage === POST_STUDIO_GENERATION_STAGE.ERROR ? (
        <div className="space-y-4">
          <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-5 text-center">
            <p className="text-sm font-semibold text-red-700">{stageTitles.error}</p>
            <p className="mt-2 text-sm leading-6 text-red-600">{stageDescriptions.error}</p>
          </div>
          <div className="flex gap-3">
            <Button
              type="button"
              variant="outline"
              className="flex-1 rounded-[8px] border-black bg-white text-sm font-bold shadow-[0px_4px_0px_0px_#000000]"
              onClick={onClose}
            >
              Close
            </Button>
            <Button
              type="button"
              className="flex-1 rounded-[8px] border border-black text-sm font-bold shadow-[0px_4px_0px_0px_#000000]"
              backgroundColor="#0D41E1"
              onClick={onRegenerate}
            >
              Try again
            </Button>
          </div>
        </div>
      ) : generationStage !== POST_STUDIO_GENERATION_STAGE.READY ? (
        <div className="space-y-4">
          <div className="flex items-center gap-1 justify-between">
            {GENERATION_STAGE_ORDER.map((step) => {
              const isActive = step === generationStage;
              const isDone =
                (generationStage === "generation" && step === "analysis") ||
                (generationStage === "finalizing" && (step === "analysis" || step === "generation"));

              return (
                <span
                  key={step}
                  className={cn(
                    "inline-flex h-7.5 items-center gap-1 rounded-full border px-2.5 transition-all duration-300",
                    isActive
                      ? "trailing-border-full border-none bg-gradient-to-r from-[#FFF8FC] to-[#FFF1F9] text-[#ED5AA9] shadow-[0px_2px_8px_rgba(237,90,169,0.12)] scale-[1.02]"
                      : isDone
                        ? "border-[#D1FADF] bg-[#F6FEF9] text-[#039855]"
                        : "border-[#F2F4F7] bg-[#FCFCFD] text-[#667085]",
                  )}
                >
                  <span
                    className={cn(
                      "h-1.5 w-1.5 rounded-full transition-all duration-500",
                      isActive
                        ? "animate-pulse bg-[#ED5AA9] shadow-[0_0_8px_rgba(237,90,169,0.6)]"
                        : isDone
                          ? "bg-[#32D583]"
                          : "bg-[#D0D5DD]",
                    )}
                  />
                  <span className={cn(
                    "text-[10px] sm:text-xs whitespace-nowrap",
                    isActive ? "font-semibold" : "font-medium"
                  )}>
                    {stageLabels[step]}
                  </span>
                </span>
              );
            })}
          </div>

          <div className="space-y-2">
            <p className="text-sm font-semibold text-[#344054]">{stageTitles[generationStage]}</p>
            <p className="text-sm leading-6 text-[#667085]">{stageDescriptions[generationStage]}</p>
          </div>

          <div className="animate-ps-shimmer relative overflow-hidden rounded-2xl border border-black bg-white px-5 py-6 shadow-[0px_4px_0px_0px_#000000]">
            <div className="relative z-20 space-y-6">
              <div className="flex items-center justify-between">
                <div className="h-5 w-32 rounded-lg bg-[#F2F4F7]" />
                <div className="animate-ps-heartbeat flex h-14 w-14 items-center justify-center rounded-2xl">
                  <img src="/favicon.png" alt="Artha Link" className="h-10 w-10 object-contain" />
                </div>
              </div>

              <div className="space-y-3">
                <div className="h-3 w-full rounded-full bg-[#F2F4F7]" />
                <div className="h-3 w-[92%] rounded-full bg-[#F2F4F7]" />
                <div className="h-3 w-[96%] rounded-full bg-[#F2F4F7]" />
                <div className="h-3 w-[75%] rounded-full bg-[#F2F4F7]" />
              </div>
            </div>
          </div>

          <div className="h-2.5 overflow-hidden rounded-full bg-[#F2F4F7]">
            <div
              className="h-full rounded-full bg-linear-to-r from-[#ED5AA9] via-[#8B5CF6] to-[#0D41E1] transition-all duration-1000 ease-linear"
              style={{
                width: `${progress}%`,
              }}
            />
          </div>

          {stayOnPageLabel && (
            <p className="text-center text-[11px] font-medium text-[#667085] animate-pulse">
              {stayOnPageLabel}
            </p>
          )}
        </div>
      ) : (
        <div className="flex flex-col h-full">
          <div className="flex-1 space-y-1.5 pb-4">
            <div className="relative rounded-2xl border border-black bg-[#FCFCFD] px-4 py-4 pr-12 shadow-[0px_4px_0px_0px_#000000]">
              <button
                type="button"
                onClick={handleCopyContent}
                disabled={copyDisabled}
                className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-lg border border-[#EAECF0] bg-white text-[#667085] transition-all hover:bg-gray-50 active:scale-95"
                title="Copy content"
              >
                {isCopied ? (
                  <RoundCheckIcon className="h-4 w-4 text-[#039855]" />
                ) : (
                  <CopyIcon className="h-4 w-4" />
                )}
              </button>
              <p className="mb-3 text-sm font-semibold text-[#344054]">
                {selectedPlatformLabel ? readyHeading : draftReadyFallback}
              </p>
              <div className="text-sm font-normal">
                {generatedContent?.body
                  ? renderContent(generatedContent.body)
                  : draftReadyFallback}
              </div>
            </div>
            {copyControls}
          </div>
          {copyFailed && copyErrorMessage && (
            <p role="alert" className="text-sm text-red-700">
              {copyErrorMessage}
            </p>
          )}
          <div className="sticky bottom-0 z-20 flex gap-3 pt-4 pb-2">
            <Button
              type="button"
              variant="outline"
              onClick={onRegenerate}
              size="icon"
              disabled={isRegenerateDisabled}
              className={cn(
                "rounded-[8px] border-black bg-white p-[10px] shadow-[0px_4px_0px_0px_#000000] cursor-pointer transition-all active:translate-y-[2px] active:shadow-[0px_2px_0px_0px_#000000]",
                isRegenerateDisabled && "cursor-not-allowed border-[#D0D5DD]",
              )}
              title={regenerateLabel}
            >
              <RotateCw className={cn("h-5 w-5 text-[#101828]", generationStage !== "ready" && "animate-spin")} />
            </Button>
            <Button
              type="button"
              className="flex-1 rounded-[8px] border border-black text-sm font-bold shadow-[0px_4px_0px_0px_#000000] active:translate-y-[2px] active:shadow-[0px_2px_0px_0px_#000000]"
              backgroundColor="#0D41E1"
              onClick={onCopyAndGo}
              disabled={copyDisabled}
            >
              {selectedPlatformLabel ? copyActionLabel : draftReadyFallback}
            </Button>
          </div>
        </div>
      )}
    </BottomSheet>
  );
}
